import { StyleSheet } from 'react-native';
import {
  getSceneHeadroom,
  RANK_LADDER_COLORS,
  SCENE_HEIGHT,
  SCENE_WIDTH,
} from './rankLadder.constants';

/**
 * Fixed dark palette from "The Climb" design (SummitClimbScreen.js) — deliberately
 * not theme-derived, to match the XP celebration surfaces. Per-camp position/size are
 * applied inline in the view (dynamic per rank slot); everything static lives here.
 */
export const getRankLadderStyles = (safeAreaTop: number) => {
  const headroom = getSceneHeadroom(safeAreaTop);

  return StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: RANK_LADDER_COLORS.BACKGROUND,
    },
    scene: {
      height: SCENE_HEIGHT + headroom,
      alignItems: 'center',
    },
    // The whole scene is authored at SCENE_WIDTH: the scenery SVGs stretch to their
    // container while camp `left` values are raw scene px, so the two only agree when the
    // container is exactly SCENE_WIDTH. Pinning the column (and centring it via
    // `scene.alignItems`) keeps every camp on the trail at any viewport width; wider
    // screens get side bands in BACKGROUND, which is the sky's own base colour.
    sceneColumn: {
      width: SCENE_WIDTH,
      height: SCENE_HEIGHT + headroom,
    },
    // Full-scene background layer (sky / mountain / trail). Absolute so all three are
    // co-extensive over the whole 1640pt scene (mock uses StyleSheet.absoluteFill) — using
    // flex:1 here would split the scroll content into thirds and squash the mountain.
    sceneFill: {
      position: 'absolute',
      top: headroom,
      left: 0,
      right: 0,
      height: SCENE_HEIGHT,
    },
    // The sky alone runs to the very top: the headroom band above the scene would otherwise
    // paint flat BACKGROUND against the sky's first stop, drawing a seam right under the
    // header. The mountain/trail keep their own geometry and stay at `sceneFill`.
    skyFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: SCENE_HEIGHT + headroom,
    },
    // sticky header. Height below the safe area must stay in step with
    // SCENE_HEADER_CLEARANCE (8 + 40 + 10 = 58, leaving it a 12px gap to the scene).
    header: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 40,
      paddingTop: safeAreaTop + 8,
      paddingBottom: 10,
    },
    // Title and back arrow share this bar's centre line; the arrow was previously pinned to
    // the header's bottom, which floated it 6px above the title.
    headerBar: {
      height: 40,
      alignItems: 'center',
      justifyContent: 'center',
    },
    headerText: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 2,
      color: RANK_LADDER_COLORS.HEADER_TITLE,
    },
    backButton: {
      position: 'absolute',
      left: 4,
      top: 0,
      bottom: 0,
      width: 40,
      alignItems: 'center',
      justifyContent: 'center',
    },
    backPressed: {
      opacity: 0.6,
    },
    // a camp box is centred on its slot; `left`/`top`/`width` are applied inline
    camp: {
      position: 'absolute',
      alignItems: 'center',
    },
    badgeLabel: {
      fontSize: 10,
      fontWeight: '700',
      marginTop: 2,
      textAlign: 'center',
    },
    aboveLabel: {
      color: RANK_LADDER_COLORS.ABOVE_LABEL,
    },
    belowLabel: {
      color: RANK_LADDER_COLORS.BELOW_LABEL,
    },
    nextName: {
      fontSize: 11,
      fontWeight: '800',
      color: RANK_LADDER_COLORS.NEXT_NAME,
    },
    // summit (Icon) label + glow
    summitGlow: {
      position: 'absolute',
      top: -6,
      width: 150,
      height: 130,
      borderRadius: 75,
      backgroundColor: RANK_LADDER_COLORS.GOAT_GLOW,
    },
    summitLabel: {
      fontSize: 12,
      fontWeight: '800',
      letterSpacing: 1.4,
      color: RANK_LADDER_COLORS.SUMMIT_LABEL,
      marginTop: 2,
    },
    // next camp: dashed unlock ring + pill
    nextRing: {
      position: 'absolute',
      top: -8,
      width: 92,
      height: 92,
      borderRadius: 46,
      borderWidth: 1.5,
      borderColor: RANK_LADDER_COLORS.NEXT_RING,
      borderStyle: 'dashed',
    },
    unlockPill: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      marginTop: 3,
      backgroundColor: RANK_LADDER_COLORS.UNLOCK_BG,
      borderRadius: 9999,
      paddingVertical: 2,
      paddingHorizontal: 8,
    },
    unlockText: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.6,
      color: RANK_LADDER_COLORS.UNLOCK_TEXT,
    },
    // current camp: glow + "YOU'RE HERE" pin + rank label + XP bar
    youGlow: {
      position: 'absolute',
      top: 4,
      width: 130,
      height: 130,
      borderRadius: 65,
      backgroundColor: RANK_LADDER_COLORS.YOU_GLOW,
    },
    youPin: {
      position: 'absolute',
      top: -34,
      backgroundColor: RANK_LADDER_COLORS.PIN_BG,
      borderRadius: 9999,
      paddingVertical: 4,
      paddingHorizontal: 11,
      zIndex: 2,
      shadowColor: RANK_LADDER_COLORS.PIN_BG,
      shadowOpacity: 0.4,
      shadowRadius: 10,
      shadowOffset: { width: 0, height: 3 },
    },
    youPinText: {
      fontSize: 11,
      fontWeight: '800',
      color: RANK_LADDER_COLORS.PIN_TEXT,
    },
    youRank: {
      fontSize: 15,
      fontWeight: '800',
      color: RANK_LADDER_COLORS.YOU_RANK,
      marginTop: 2,
    },
    xpTrack: {
      width: 130,
      height: 6,
      backgroundColor: RANK_LADDER_COLORS.XP_TRACK,
      borderRadius: 9999,
      overflow: 'hidden',
      marginTop: 8,
    },
    xpFill: {
      height: '100%',
      borderRadius: 9999,
    },
    xpLabel: {
      fontSize: 10,
      color: RANK_LADDER_COLORS.XP_LABEL,
      marginTop: 5,
    },
    basecampLabel: {
      position: 'absolute',
      width: 96,
      textAlign: 'center',
      fontSize: 9,
      fontWeight: '700',
      letterSpacing: 1,
      color: RANK_LADDER_COLORS.BASECAMP_LABEL,
    },
  });
};
